/**
 * Validation Rule Builder for Custom Field Definitions.
 *
 * Provides a visual editor for field-level validation rules stored as JSON.
 * Rule types: required, regex, range, expression, valid_values.
 * See docs/architecture/field-validation-rule-writing-guide.md for full spec.
 */
import { useCallback, useMemo, useState } from "react";
import { Plus, Trash01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { cx } from "@/utils/cx";

// =============================================================================
// TYPES
// =============================================================================

interface ValidationRule {
    type: string;
    message?: string;
    severity?: string;
    params?: Record<string, unknown>;
}

interface ValidationRuleBuilderProps {
    /** JSON string of the validation rules array */
    value: string;
    /** Called with the updated JSON string */
    onChange: (json: string) => void;
    isDisabled?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const RULE_TYPES: SelectItemType[] = [
    { id: "required", label: "Required" },
    { id: "regex", label: "Regex Pattern" },
    { id: "range", label: "Numeric Range" },
    { id: "expression", label: "Expression (NCalc)" },
    { id: "valid_values", label: "Valid Values" },
];

const SEVERITY_OPTIONS: SelectItemType[] = [
    { id: "Blocking", label: "Blocking" },
    { id: "Warning", label: "Warning" },
];

const RULE_TYPE_LABELS: Record<string, string> = {
    required: "Required",
    regex: "Regex Pattern",
    range: "Numeric Range",
    expression: "Expression (NCalc)",
    valid_values: "Valid Values",
};

// =============================================================================
// PARSE / SERIALIZE HELPERS
// =============================================================================

function parseRules(json: string): ValidationRule[] | null {
    if (!json.trim()) return [];
    try {
        const parsed = JSON.parse(json);
        if (!Array.isArray(parsed)) return null;
        return parsed;
    } catch {
        return null;
    }
}

function serializeRules(rules: ValidationRule[]): string {
    if (rules.length === 0) return "";
    return JSON.stringify(rules, null, 2);
}

// =============================================================================
// INDIVIDUAL RULE CARD
// =============================================================================

interface RuleCardProps {
    rule: ValidationRule;
    index: number;
    onChange: (index: number, rule: ValidationRule) => void;
    onRemove: (index: number) => void;
    isDisabled?: boolean;
}

function RuleCard({ rule, index, onChange, onRemove, isDisabled }: RuleCardProps) {
    const updateField = useCallback(
        (key: keyof ValidationRule, value: unknown) => {
            onChange(index, { ...rule, [key]: value });
        },
        [index, onChange, rule],
    );

    const updateParam = useCallback(
        (paramKey: string, value: unknown) => {
            const params = { ...(rule.params ?? {}), [paramKey]: value };
            onChange(index, { ...rule, params });
        },
        [index, onChange, rule],
    );

    return (
        <div className="space-y-3 rounded-lg border border-secondary bg-primary p-3">
            {/* Header row */}
            <div className="flex items-center gap-2">
                <span className="rounded bg-brand-secondary px-2 py-0.5 text-xs font-semibold text-brand-tertiary">
                    {RULE_TYPE_LABELS[rule.type] ?? rule.type}
                </span>
                <span className={cx("ml-auto text-xs font-medium", rule.severity === "Warning" ? "text-warning-primary" : "text-error-primary")}>
                    {rule.severity ?? "Blocking"}
                </span>
                {!isDisabled && (
                    <button
                        type="button"
                        className="cursor-pointer rounded p-1 text-quaternary transition-colors hover:bg-error-secondary hover:text-error-primary"
                        onClick={() => onRemove(index)}
                        title="Remove rule"
                    >
                        <Trash01 className="size-4" />
                    </button>
                )}
            </div>

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-3">
                <Select
                    label="Type"
                    items={RULE_TYPES}
                    selectedKey={rule.type}
                    onSelectionChange={(key) => {
                        // Reset params when type changes
                        onChange(index, { type: String(key), message: rule.message, severity: rule.severity });
                    }}
                    size="sm"
                    isDisabled={isDisabled}
                >
                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
                <Select
                    label="Severity"
                    items={SEVERITY_OPTIONS}
                    selectedKey={rule.severity ?? "Blocking"}
                    onSelectionChange={(key) => updateField("severity", String(key))}
                    size="sm"
                    isDisabled={isDisabled}
                >
                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
            </div>

            <Input
                label="Error Message"
                value={rule.message ?? ""}
                onChange={(v) => updateField("message", v)}
                size="sm"
                isDisabled={isDisabled}
                placeholder="Message shown when validation fails"
            />

            {/* Type-specific params */}
            {rule.type === "regex" && (
                <Input
                    label="Pattern"
                    value={String(rule.params?.pattern ?? "")}
                    onChange={(v) => updateParam("pattern", v)}
                    size="sm"
                    isDisabled={isDisabled}
                    placeholder="^INV-\\d{6}$"
                />
            )}

            {rule.type === "range" && (
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Min"
                        value={rule.params?.min != null ? String(rule.params.min) : ""}
                        onChange={(v) => updateParam("min", v ? Number(v) : undefined)}
                        size="sm"
                        type="number"
                        isDisabled={isDisabled}
                        placeholder="0"
                    />
                    <Input
                        label="Max"
                        value={rule.params?.max != null ? String(rule.params.max) : ""}
                        onChange={(v) => updateParam("max", v ? Number(v) : undefined)}
                        size="sm"
                        type="number"
                        isDisabled={isDisabled}
                        placeholder="999999"
                    />
                </div>
            )}

            {rule.type === "expression" && (
                <Input
                    label="Expression"
                    value={String(rule.params?.expression ?? "")}
                    onChange={(v) => updateParam("expression", v)}
                    size="sm"
                    isDisabled={isDisabled}
                    placeholder="this > 0 && Floor(this) == this"
                />
            )}

            {rule.type === "valid_values" && (
                <ValidValuesEditor
                    values={(rule.params?.values as string[]) ?? []}
                    onChange={(values) => updateParam("values", values)}
                    isDisabled={isDisabled}
                />
            )}
        </div>
    );
}

// =============================================================================
// VALID VALUES EDITOR (tag-style)
// =============================================================================

function ValidValuesEditor({ values, onChange, isDisabled }: { values: string[]; onChange: (v: string[]) => void; isDisabled?: boolean }) {
    const [input, setInput] = useState("");

    const handleAdd = useCallback(() => {
        const trimmed = input.trim();
        if (!trimmed || values.includes(trimmed)) return;
        onChange([...values, trimmed]);
        setInput("");
    }, [input, values, onChange]);

    const handleRemove = useCallback(
        (idx: number) => {
            onChange(values.filter((_, i) => i !== idx));
        },
        [values, onChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
            }
        },
        [handleAdd],
    );

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-secondary">Valid Values</label>
            {values.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {values.map((v, i) => (
                        <span
                            key={`${v}-${i}`}
                            className="inline-flex items-center gap-1 rounded-full border border-secondary bg-secondary_subtle px-2.5 py-0.5 text-xs font-medium text-primary"
                        >
                            {v}
                            {!isDisabled && (
                                <button
                                    type="button"
                                    className="cursor-pointer rounded-full p-0.5 text-quaternary hover:text-error-primary"
                                    onClick={() => handleRemove(i)}
                                >
                                    &times;
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            {!isDisabled && (
                <div className="flex gap-2">
                    <Input value={input} onChange={setInput} size="sm" placeholder="Add a value..." className="flex-1" onKeyDown={handleKeyDown} />
                    <Button color="secondary" size="sm" onClick={handleAdd} isDisabled={!input.trim()}>
                        Add
                    </Button>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// MAIN BUILDER COMPONENT
// =============================================================================

export function ValidationRuleBuilder({ value, onChange, isDisabled }: ValidationRuleBuilderProps) {
    const [rawMode, setRawMode] = useState(false);

    const parsed = useMemo(() => parseRules(value), [value]);
    const isMalformed = parsed === null;

    // Force raw mode if JSON is malformed
    const effectiveRawMode = rawMode || isMalformed;

    const handleRuleChange = useCallback(
        (index: number, updatedRule: ValidationRule) => {
            if (!parsed) return;
            const next = [...parsed];
            next[index] = updatedRule;
            onChange(serializeRules(next));
        },
        [parsed, onChange],
    );

    const handleRemoveRule = useCallback(
        (index: number) => {
            if (!parsed) return;
            const next = parsed.filter((_, i) => i !== index);
            onChange(serializeRules(next));
        },
        [parsed, onChange],
    );

    const handleAddRule = useCallback(() => {
        const rules = parsed ?? [];
        const newRule: ValidationRule = {
            type: "required",
            message: "",
            severity: "Blocking",
        };
        onChange(serializeRules([...rules, newRule]));
    }, [parsed, onChange]);

    return (
        <div className="space-y-3">
            {/* Toggle between visual and raw modes */}
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-secondary">Validation Rules</label>
                <button
                    type="button"
                    className="cursor-pointer text-xs font-medium text-brand-tertiary transition-colors hover:text-brand-primary"
                    onClick={() => setRawMode(!rawMode)}
                >
                    {effectiveRawMode ? "Visual Editor" : "Raw JSON"}
                </button>
            </div>

            {isMalformed && !rawMode && <p className="text-xs text-warning-primary">Invalid JSON — switch to Raw JSON to fix, or clear the field.</p>}

            {effectiveRawMode ? (
                <TextArea
                    value={value}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    placeholder='[{"type": "required", "message": "Field is required"}]'
                    rows={8}
                    textAreaClassName="font-mono text-xs"
                />
            ) : (
                <>
                    {parsed && parsed.length === 0 && <p className="text-xs text-quaternary">No validation rules. Click "Add Rule" to create one.</p>}
                    {parsed?.map((rule, idx) => (
                        <RuleCard key={idx} rule={rule} index={idx} onChange={handleRuleChange} onRemove={handleRemoveRule} isDisabled={isDisabled} />
                    ))}
                    {!isDisabled && (
                        <Button color="secondary" size="sm" iconLeading={Plus} onClick={handleAddRule}>
                            Add Rule
                        </Button>
                    )}
                </>
            )}
        </div>
    );
}
